"use client";

import { Suspense, useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { ArrowRight, User, Mail, CheckCircle, Shield, X } from "lucide-react";
import { SiteNav, SiteFooter } from "@/components/landing/chrome";
import { STYLES as BASE_STYLES } from "@/components/landing/LandingV2";
import { sendToCRM } from "@/utils/crm";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

interface FormData {
  name: string;
  email: string;
  phone: string;
}

const GETTING_STARTED_STYLES = `
.lvlup-landing .gs-page { padding: 80px 0 96px; }
@media (max-width: 720px) { .lvlup-landing .gs-page { padding: 56px 0 64px; } }

.lvlup-landing .gs-head { text-align: center; max-width: 720px; margin: 0 auto 56px; }
.lvlup-landing .gs-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  background: var(--accent-soft); border: 1px solid var(--accent-line);
  color: var(--accent);
  font-family: var(--mono); font-size: 11px; letter-spacing: 0.5px;
  text-transform: uppercase; font-weight: 600;
  padding: 8px 14px; border-radius: 999px;
  margin-bottom: 20px;
}
.lvlup-landing .gs-head h1 {
  font-family: var(--display);
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 700; letter-spacing: -1.8px; line-height: 1;
  margin: 0 0 18px; color: var(--ink); text-wrap: balance;
}
.lvlup-landing .gs-head h1 .lp-accent { color: var(--accent); }
.lvlup-landing .gs-head p { font-size: 17px; color: var(--ink-dim); margin: 0; text-wrap: pretty; }

.lvlup-landing .gs-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 28px;
  align-items: start;
}
@media (max-width: 880px) { .lvlup-landing .gs-grid { grid-template-columns: 1fr; gap: 20px; } }

.lvlup-landing .gs-card {
  background: var(--bg-raised);
  border: 1px solid var(--line);
  border-radius: 20px;
  padding: 32px;
}
@media (max-width: 720px) { .lvlup-landing .gs-card { padding: 24px; border-radius: 16px; } }
.lvlup-landing .gs-card-pro {
  border-color: var(--accent-line);
  background: linear-gradient(180deg, oklch(0.88 0.18 120 / 0.06), transparent 60%), var(--bg-raised);
}
.lvlup-landing .gs-card h2 {
  font-family: var(--display); font-size: 22px; font-weight: 700;
  letter-spacing: -0.5px; margin: 0 0 24px; color: var(--ink);
}
.lvlup-landing .gs-card h3 {
  font-family: var(--display); font-size: 20px; font-weight: 700;
  letter-spacing: -0.4px; margin: 0 0 8px; color: var(--accent);
}

.lvlup-landing .gs-field { margin-bottom: 18px; }
.lvlup-landing .gs-label {
  display: block; font-family: var(--mono); font-size: 11px;
  letter-spacing: 0.5px; text-transform: uppercase;
  color: var(--ink-dim); margin-bottom: 8px; font-weight: 600;
}
.lvlup-landing .gs-input-wrap { position: relative; }
.lvlup-landing .gs-input-icon {
  position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
  color: var(--ink-faint); pointer-events: none;
}
.lvlup-landing .gs-input {
  width: 100%; padding: 13px 14px 13px 42px;
  background: var(--bg-deep); border: 1px solid var(--line-strong);
  border-radius: 12px; color: var(--ink); font-family: var(--ui); font-size: 15px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.lvlup-landing .gs-input::placeholder { color: var(--ink-faint); }
.lvlup-landing .gs-input:focus {
  outline: none; border-color: var(--accent);
  box-shadow: 0 0 0 3px oklch(0.88 0.18 120 / 0.15);
}
.lvlup-landing .gs-phone-wrap { padding-left: 0; }
.lvlup-landing .gs-phone-wrap .PhoneInput {
  display: flex; align-items: center; gap: 10px;
  padding: 4px 12px;
  background: var(--bg-deep); border: 1px solid var(--line-strong);
  border-radius: 12px;
}
.lvlup-landing .gs-phone-wrap .PhoneInputCountry { padding: 6px 8px; border-right: 1px solid var(--line); }
.lvlup-landing .gs-phone-wrap .PhoneInputCountryIcon { box-shadow: none; background: transparent; }
.lvlup-landing .gs-phone-wrap .PhoneInputInput {
  flex: 1; padding: 10px 0; background: transparent; border: none; outline: none;
  color: var(--ink); font-family: var(--ui); font-size: 15px;
}
.lvlup-landing .gs-phone-wrap .PhoneInputInput::placeholder { color: var(--ink-faint); }
.lvlup-landing .gs-phone-wrap .PhoneInputCountrySelectArrow { color: var(--ink-dim); opacity: 1; }

.lvlup-landing .gs-submit {
  width: 100%; margin-top: 8px;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  background: var(--accent); color: var(--accent-ink);
  font-family: var(--ui); font-weight: 700; font-size: 15px;
  padding: 14px 22px; border-radius: 12px;
  border: 1px solid var(--accent);
  cursor: pointer; transition: filter 0.15s, transform 0.1s;
}
.lvlup-landing .gs-submit:hover:not(:disabled) { filter: brightness(1.05); }
.lvlup-landing .gs-submit:active:not(:disabled) { transform: scale(0.99); }
.lvlup-landing .gs-submit:disabled { opacity: 0.5; cursor: not-allowed; }

.lvlup-landing .gs-price {
  font-family: var(--display); font-size: 40px; font-weight: 700;
  letter-spacing: -1.5px; color: var(--ink); line-height: 1;
  margin-bottom: 18px;
}
.lvlup-landing .gs-savings {
  display: inline-block;
  background: var(--accent-soft); color: var(--accent);
  border: 1px solid var(--accent-line);
  font-family: var(--mono); font-size: 11px; letter-spacing: 0.5px;
  padding: 6px 12px; border-radius: 999px;
  margin-bottom: 18px;
}
.lvlup-landing .gs-features-title {
  font-family: var(--mono); font-size: 11px; font-weight: 600;
  letter-spacing: 0.5px; text-transform: uppercase;
  color: var(--ink-dim); margin: 22px 0 14px;
  padding-top: 22px; border-top: 1px solid var(--line);
}
.lvlup-landing .gs-feature-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 12px; }
.lvlup-landing .gs-feature-list li {
  display: flex; align-items: flex-start; gap: 10px;
  font-size: 14.5px; color: var(--ink); line-height: 1.5;
}
.lvlup-landing .gs-feature-list li svg { color: var(--accent); flex-shrink: 0; margin-top: 3px; }

.lvlup-landing .gs-next {
  margin-top: 24px; padding: 16px 18px;
  background: var(--bg-deep); border: 1px solid var(--line);
  border-radius: 12px;
  font-size: 13.5px; color: var(--ink-dim); line-height: 1.55;
}
.lvlup-landing .gs-next strong { color: var(--ink); font-weight: 600; }

.lvlup-landing .gs-commit-label {
  display: inline-block; margin-top: 18px;
  font-family: var(--mono); font-size: 11px;
  color: var(--ink-faint); letter-spacing: 0.4px;
}

.lvlup-landing .gs-loading {
  min-height: 60vh; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 14px;
}
.lvlup-landing .gs-spinner {
  width: 36px; height: 36px;
  border: 3px solid var(--line); border-top-color: var(--accent);
  border-radius: 50%;
  animation: gs-spin 0.9s linear infinite;
}
@keyframes gs-spin { to { transform: rotate(360deg); } }
.lvlup-landing .gs-loading h1 { font-family: var(--display); font-size: 22px; font-weight: 600; color: var(--ink); margin: 0; }
.lvlup-landing .gs-loading p { font-size: 14px; color: var(--ink-faint); margin: 0; }

/* Consent modal */
.lvlup-landing .gs-modal-backdrop {
  position: fixed; inset: 0; z-index: 100;
  background: oklch(0 0 0 / 0.7);
  backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
}
.lvlup-landing .gs-modal {
  background: var(--bg-raised); border: 1px solid var(--line);
  border-radius: 20px;
  max-width: 600px; width: 100%; max-height: 90vh; overflow-y: auto;
  box-shadow: 0 50px 100px -20px rgba(0,0,0,0.7);
}
.lvlup-landing .gs-modal-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 22px 28px;
  border-bottom: 1px solid var(--line);
}
.lvlup-landing .gs-modal-head h2 {
  font-family: var(--display); font-size: 19px; font-weight: 700;
  letter-spacing: -0.3px; margin: 0; color: var(--ink);
  display: flex; align-items: center; gap: 12px;
}
.lvlup-landing .gs-modal-head h2 svg { color: var(--accent); }
.lvlup-landing .gs-modal-close {
  background: transparent; border: none; padding: 6px;
  color: var(--ink-faint); cursor: pointer; transition: color 0.15s;
}
.lvlup-landing .gs-modal-close:hover { color: var(--ink); }
.lvlup-landing .gs-modal-body { padding: 24px 28px; }
.lvlup-landing .gs-modal-body > p { font-size: 14.5px; color: var(--ink-dim); margin: 0 0 20px; line-height: 1.55; }

.lvlup-landing .gs-consents { display: flex; flex-direction: column; gap: 10px; margin-bottom: 22px; }
.lvlup-landing .gs-consent {
  display: flex; align-items: flex-start; gap: 12px;
  padding: 16px;
  background: var(--bg-deep); border: 1px solid var(--line);
  border-radius: 12px;
  cursor: pointer; transition: border-color 0.15s;
}
.lvlup-landing .gs-consent:hover { border-color: var(--accent-line); }
.lvlup-landing .gs-consent input[type="checkbox"] {
  appearance: none; -webkit-appearance: none;
  width: 18px; height: 18px;
  border: 1px solid var(--line-strong); border-radius: 4px;
  background: var(--bg-raised); cursor: pointer; flex-shrink: 0; margin-top: 1px;
  position: relative; transition: all 0.15s;
}
.lvlup-landing .gs-consent input[type="checkbox"]:checked {
  background: var(--accent); border-color: var(--accent);
}
.lvlup-landing .gs-consent input[type="checkbox"]:checked::after {
  content: ''; position: absolute; left: 5px; top: 1px;
  width: 5px; height: 10px;
  border: solid var(--accent-ink);
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.lvlup-landing .gs-consent span { font-size: 13.5px; color: var(--ink); line-height: 1.55; }

.lvlup-landing .gs-guarantee {
  display: flex; align-items: flex-start; gap: 12px;
  padding: 16px; margin-bottom: 20px;
  background: var(--accent-soft); border: 1px solid var(--accent-line);
  border-radius: 12px;
}
.lvlup-landing .gs-guarantee svg { color: var(--accent); flex-shrink: 0; margin-top: 2px; }
.lvlup-landing .gs-guarantee h4 {
  font-family: var(--display); font-size: 14px; font-weight: 700;
  letter-spacing: -0.2px; margin: 0 0 4px; color: var(--accent);
}
.lvlup-landing .gs-guarantee p { font-size: 13px; color: var(--ink); margin: 0; line-height: 1.55; }

.lvlup-landing .gs-modal-actions { display: flex; gap: 10px; }
@media (max-width: 520px) { .lvlup-landing .gs-modal-actions { flex-direction: column; } }
.lvlup-landing .gs-btn-secondary {
  flex: 1; padding: 13px 22px;
  background: transparent; border: 1px solid var(--line-strong);
  color: var(--ink); font-family: var(--ui); font-weight: 600; font-size: 14px;
  border-radius: 12px; cursor: pointer; transition: border-color 0.15s, color 0.15s;
}
.lvlup-landing .gs-btn-secondary:hover { border-color: var(--ink-dim); }
`;

function GettingStartedContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [plan, setPlan] = useState<string>("");
  const [billing, setBilling] = useState<string>("monthly");
  const [isInitializing, setIsInitializing] = useState(true);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentAgreed, setConsentAgreed] = useState({
    booking: false,
    attendance: false,
  });

  useEffect(() => {
    const planParam = searchParams.get("plan");
    const billingParam = searchParams.get("billing");
    if (planParam) setPlan(planParam);
    if (billingParam) setBilling(billingParam);

    const timer = setTimeout(() => setIsInitializing(false), 400);
    return () => clearTimeout(timer);
  }, [searchParams]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleConsentChange = (consentType: keyof typeof consentAgreed) => {
    setConsentAgreed((prev) => ({ ...prev, [consentType]: !prev[consentType] }));
  };

  const handleConsentSubmit = async () => {
    if (!isConsentValid) return;

    setIsLoading(true);
    setShowConsentModal(false);

    try {
      await sendToCRM({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        plan,
        billing,
        status: "consent_completed",
      });

      const priceId = "price_1S7aUCKIAq30uRaMXTYUDRJv";

      const response = await fetch("/api/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          priceId,
          customerEmail: formData.email,
          customerName: formData.name,
          successUrl: `${window.location.origin}/checkout-success?plan=pro&email=${encodeURIComponent(
            formData.email,
          )}&name=${encodeURIComponent(formData.name)}&phone=${encodeURIComponent(
            formData.phone,
          )}&billing=${billing}`,
          cancelUrl: `${window.location.origin}/getting-started?plan=pro&billing=${billing}`,
        }),
      });

      const { url } = await response.json();
      if (url) window.location.href = url;
    } catch (error) {
      console.error("Error processing consent:", error);
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (plan === "pro" && billing === "monthly" && !showConsentModal) {
      await sendToCRM({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        plan,
        billing,
        status: "profile_created",
      });

      setShowConsentModal(true);
      return;
    }

    setIsLoading(true);

    try {
      localStorage.setItem("userGettingStarted", JSON.stringify(formData));

      if (plan === "pro" && billing === "annual") {
        await sendToCRM({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          plan,
          billing,
          status: "profile_created",
        });

        const priceId = "price_1S7aUCKIAq30uRaMGQrEWGRA";

        const response = await fetch("/api/create-checkout-session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            priceId,
            customerEmail: formData.email,
            customerName: formData.name,
            successUrl: `${window.location.origin}/checkout-success?plan=pro&email=${encodeURIComponent(
              formData.email,
            )}&name=${encodeURIComponent(formData.name)}&phone=${encodeURIComponent(
              formData.phone,
            )}&billing=${billing}`,
            cancelUrl: `${window.location.origin}/getting-started?plan=pro&billing=${billing}`,
          }),
        });

        const { url } = await response.json();
        if (url) window.location.href = url;
      } else if (plan === "high-value") {
        await sendToCRM({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          plan,
          status: "profile_created",
        });

        router.push("/discover");
      }
    } catch (error) {
      console.error("Error processing form:", error);
      setIsLoading(false);
    }
  };

  const isFormValid = formData.name && formData.email && formData.phone;
  const isConsentValid = Object.values(consentAgreed).every((agreed) => agreed);

  const getPlanDetails = () => {
    switch (plan) {
      case "pro":
        return {
          title: "Pro Plan",
          price: billing === "annual" ? "$780/year" : "$78/month",
          features: [
            "Everything in Basic",
            "Your own branded mobile app",
            "App live in stores within 20 days",
            "Complete white-label platform",
            "Custom app icon & branding",
            "Priority support",
          ],
        };
      case "high-value":
        return {
          title: "High Value Coaching",
          price: "Custom Pricing",
          features: [
            "Everything in Pro",
            "Ready-made programming",
            "Follow-along video library",
            "Course creation tools",
            "Engagement challenges",
            "Advanced app customization",
            "Multiple team members",
            "Dedicated success manager",
          ],
        };
      default:
        return null;
    }
  };

  const planDetails = getPlanDetails();

  return (
    <div className="lvlup-landing">
      <style dangerouslySetInnerHTML={{ __html: BASE_STYLES + GETTING_STARTED_STYLES }} />
      <SiteNav />

      <main>
        {isInitializing ? (
          <section className="gs-loading">
            <div className="gs-spinner" />
            <h1>Loading…</h1>
            <p>Setting up your plan details</p>
          </section>
        ) : !planDetails ? (
          <section className="gs-loading">
            <h1>Invalid plan</h1>
            <p>Please select a valid plan from our pricing page.</p>
            <a href="/pricing" className="lp-btn lp-btn-primary" style={{ marginTop: 12 }}>
              Back to pricing
            </a>
          </section>
        ) : (
          <section className="gs-page">
            <div className="lp-container">
              <div className="gs-head">
                <span className="gs-eyebrow">
                  <CheckCircle size={14} /> Great choice
                </span>
                <h1>
                  Let&apos;s get you <span className="lp-accent">started</span>
                </h1>
                <p>
                  Just a few details and you&apos;ll be on your way to transforming your coaching business.
                </p>
              </div>

              <div className="gs-grid">
                {/* Form card */}
                <div className="gs-card">
                  <h2>Your information</h2>
                  <form onSubmit={handleSubmit}>
                    <div className="gs-field">
                      <label htmlFor="name" className="gs-label">Full name</label>
                      <div className="gs-input-wrap">
                        <User size={18} className="gs-input-icon" />
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="gs-input"
                          placeholder="Enter your full name"
                        />
                      </div>
                    </div>

                    <div className="gs-field">
                      <label htmlFor="email" className="gs-label">Email address</label>
                      <div className="gs-input-wrap">
                        <Mail size={18} className="gs-input-icon" />
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="gs-input"
                          placeholder="you@example.com"
                        />
                      </div>
                    </div>

                    <div className="gs-field">
                      <label htmlFor="phone" className="gs-label">Phone number</label>
                      <div className="gs-phone-wrap">
                        <PhoneInput
                          international
                          countryCallingCodeEditable={false}
                          defaultCountry="US"
                          value={formData.phone}
                          onChange={(value) => setFormData((prev) => ({ ...prev, phone: value || "" }))}
                          placeholder="Enter your phone number"
                        />
                      </div>
                    </div>

                    <button type="submit" disabled={!isFormValid || isLoading} className="gs-submit">
                      {isLoading ? (
                        <div className="gs-spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                      ) : (
                        <>
                          Continue
                          <ArrowRight size={18} />
                        </>
                      )}
                    </button>
                  </form>
                </div>

                {/* Plan summary card */}
                <div className="gs-card gs-card-pro">
                  <h2>Plan summary</h2>
                  <h3>{planDetails.title}</h3>
                  <div className="gs-price">{planDetails.price}</div>
                  {billing === "annual" && plan === "pro" && (
                    <div className="gs-savings">Save $156/year with annual billing</div>
                  )}

                  <div className="gs-features-title">What&apos;s included</div>
                  <ul className="gs-feature-list">
                    {planDetails.features.map((f) => (
                      <li key={f}>
                        <CheckCircle size={16} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {plan === "pro" && (
                    <span className="gs-commit-label">Minimum commitment: 1 year</span>
                  )}

                  {plan === "high-value" && (
                    <div className="gs-next">
                      <strong>Next step:</strong> You&apos;ll be connected with our team to discuss your specific
                      needs and custom pricing.
                    </div>
                  )}

                  {plan === "pro" && (
                    <div className="gs-next">
                      <strong>Next step:</strong> Complete your payment and we&apos;ll get your branded app
                      ready within 20 days.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      <SiteFooter />

      {showConsentModal && (
        <div className="gs-modal-backdrop" onClick={() => setShowConsentModal(false)}>
          <div className="gs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gs-modal-head">
              <h2>
                <Shield size={20} />
                Quick commitments
              </h2>
              <button
                type="button"
                onClick={() => setShowConsentModal(false)}
                className="gs-modal-close"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="gs-modal-body">
              <p>Before proceeding with your Pro Monthly plan, please confirm:</p>

              <div className="gs-consents">
                <label className="gs-consent">
                  <input
                    type="checkbox"
                    checked={consentAgreed.booking}
                    onChange={() => handleConsentChange("booking")}
                  />
                  <span>
                    I will be responsible for choosing and booking a slot to discuss my coaching business
                    and app details with the LVLUP team.
                  </span>
                </label>

                <label className="gs-consent">
                  <input
                    type="checkbox"
                    checked={consentAgreed.attendance}
                    onChange={() => handleConsentChange("attendance")}
                  />
                  <span>
                    I commit to showing up for my scheduled booking and actively participating in the
                    discussion.
                  </span>
                </label>
              </div>

              <div className="gs-guarantee">
                <CheckCircle size={18} />
                <div>
                  <h4>Money-back guarantee</h4>
                  <p>
                    If you fulfill the above commitments and we don&apos;t deliver your branded mobile app
                    within 20 days as promised, we guarantee a full refund of your subscription.
                  </p>
                </div>
              </div>

              <div className="gs-modal-actions">
                <button
                  type="button"
                  onClick={() => setShowConsentModal(false)}
                  className="gs-btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConsentSubmit}
                  disabled={!isConsentValid || isLoading}
                  className="gs-submit"
                  style={{ flex: 1, marginTop: 0 }}
                >
                  {isLoading ? (
                    <div className="gs-spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                  ) : (
                    <>
                      Proceed to checkout
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GettingStartedLoading() {
  return (
    <div className="lvlup-landing">
      <style dangerouslySetInnerHTML={{ __html: BASE_STYLES + GETTING_STARTED_STYLES }} />
      <SiteNav />
      <main>
        <section className="gs-loading">
          <div className="gs-spinner" />
          <h1>Loading…</h1>
          <p>Setting up your plan details</p>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

export default function GettingStartedClient() {
  return (
    <Suspense fallback={<GettingStartedLoading />}>
      <GettingStartedContent />
    </Suspense>
  );
}
